import { getCountryCallingCode, isSupportedCountry } from 'libphonenumber-js';
import { assoc } from 'ramda';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { Flag } from '../../icons/flag';
import { Flex } from '../flex';
import { Icon } from '../icon';
import { Input, InputGroup, InputLeftElement } from '../input';
import { Select } from '../select';
// prettier-ignore
const countryCodes = [
    'ad',
    'ae',
    'af',
    'ag',
    'ai',
    'al',
    'am',
    'ao',
    'aq',
    'ar',
    'as',
    'at',
    'au',
    'aw',
    'ax',
    'az',
    'ba',
    'bb',
    'bd',
    'be',
    'bf',
    'bg',
    'bh',
    'bi',
    'bj',
    'bl',
    'bm',
    'bn',
    'bo',
    'bq',
    'br',
    'bs',
    'bt',
    'bv',
    'bw',
    'by',
    'bz',
    'ca',
    'cc',
    'cd',
    'cf',
    'cg',
    'ch',
    'ci',
    'ck',
    'cl',
    'cm',
    'cn',
    'co',
    'cr',
    'cu',
    'cv',
    'cw',
    'cx',
    'cy',
    'cz',
    'de',
    'dj',
    'dk',
    'dm',
    'do',
    'dz',
    'ec',
    'ee',
    'eg',
    'eh',
    'er',
    'es',
    'et',
    'eu',
    'fi',
    'fj',
    'fk',
    'fm',
    'fo',
    'fr',
    'ga',
    'gb',
    'gd',
    'ge',
    'gf',
    'gg',
    'gh',
    'gi',
    'gl',
    'gm',
    'gn',
    'gp',
    'gq',
    'gr',
    'gs',
    'gt',
    'gu',
    'gw',
    'gy',
    'hk',
    'hm',
    'hn',
    'hr',
    'ht',
    'hu',
    'id',
    'ie',
    'il',
    'im',
    'in',
    'io',
    'iq',
    'ir',
    'is',
    'it',
    'je',
    'jm',
    'jo',
    'jp',
    'ke',
    'kg',
    'kh',
    'ki',
    'km',
    'kn',
    'kp',
    'kr',
    'kw',
    'ky',
    'kz',
    'la',
    'lb',
    'lc',
    'li',
    'lk',
    'lr',
    'ls',
    'lt',
    'lu',
    'lv',
    'ly',
    'ma',
    'mc',
    'md',
    'me',
    'mf',
    'mg',
    'mh',
    'mk',
    'ml',
    'mm',
    'mn',
    'mo',
    'mp',
    'mq',
    'mr',
    'ms',
    'mt',
    'mu',
    'mv',
    'mw',
    'mx',
    'my',
    'mz',
    'na',
    'nc',
    'ne',
    'nf',
    'ng',
    'ni',
    'nl',
    'no',
    'np',
    'nr',
    'nu',
    'nz',
    'om',
    'pa',
    'pe',
    'pf',
    'pg',
    'ph',
    'pk',
    'pl',
    'pm',
    'pn',
    'pr',
    'ps',
    'pt',
    'pw',
    'py',
    'qa',
    're',
    'ro',
    'rs',
    'ru',
    'rw',
    'sa',
    'sb',
    'sc',
    'sd',
    'se',
    'sg',
    'sh',
    'si',
    'sj',
    'sk',
    'sl',
    'sm',
    'sn',
    'so',
    'sr',
    'ss',
    'st',
    'sv',
    'sx',
    'sy',
    'sz',
    'tc',
    'td',
    'tf',
    'tg',
    'th',
    'tj',
    'tk',
    'tl',
    'tm',
    'tn',
    'to',
    'tr',
    'tt',
    'tv',
    'tw',
    'tz',
    'ua',
    'ug',
    'um',
    'un',
    'us',
    'uy',
    'uz',
    'va',
    'vc',
    've',
    'vg',
    'vi',
    'vn',
    'vu',
    'wf',
    'ws',
    'xk',
    'ye',
    'yt',
    'za',
    'zm',
    'zw'
];
function getCountries(language) {
    const countryName = new Intl.DisplayNames(language, {
        type: 'region',
        style: 'short'
    });
    return countryCodes.reduce((acc, code)=>assoc(code, {
            code,
            name: countryName.of(code.toUpperCase())?.split('(')[0].trim(),
            prefix: isSupportedCountry(code.toUpperCase()) ? getCountryCallingCode(code.toUpperCase()) : ''
        }, acc), {});
}
// eslint-disable-next-line react/display-name
export const PhoneInput = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { value , placeholder , language , onChange , ...inputProps } = props;
    const countries = useMemo(()=>getCountries(language), [
        language
    ]);
    const countryOptions = useMemo(()=>Object.values(countries), [
        countries
    ]);
    const [country, setCountry] = useState(value?.country);
    const [phoneNumber, setPhoneNumber] = useState(value?.phoneNumber);
    // Hide countries on the server to prevent a hydration error
    const [showCountries, setShowCountries] = useState(false);
    useEffect(()=>setShowCountries(true), [
        setShowCountries
    ]);
    const handleCountryChange = (event)=>{
        const newCountry = event.target.value;
        setCountry(newCountry);
        onChange({
            country: newCountry,
            phoneNumber
        });
    };
    const handleNumberChange = (event)=>{
        const newPhoneNumber = event.target.value;
        setPhoneNumber(newPhoneNumber);
        onChange({
            country,
            phoneNumber: newPhoneNumber
        });
    };
    return /*#__PURE__*/ React.createElement(InputGroup, null, /*#__PURE__*/ React.createElement(InputLeftElement, {
        w: "4rem"
    }, /*#__PURE__*/ React.createElement(Select, {
        left: 0,
        onChange: handleCountryChange,
        opacity: 0,
        pos: "absolute",
        size: "xs",
        top: 0,
        value: country,
        zIndex: 1
    }, /*#__PURE__*/ React.createElement("option", {
        value: ""
    }), showCountries && countryOptions.map(({ code , name  })=>/*#__PURE__*/ React.createElement("option", {
            key: code,
            value: code
        }, name, countries[code].prefix !== '' && ` (+${countries[code].prefix})`))), /*#__PURE__*/ React.createElement(Flex, {
        pl: 2,
        w: "100%"
    }, country ? /*#__PURE__*/ React.createElement("img", {
        alt: countries[country].name,
        height: "18",
        src: `https://flagcdn.com/24x18/${country}.webp`,
        width: "24"
    }) : /*#__PURE__*/ React.createElement(Icon, {
        as: Flag,
        boxSize: "24px"
    }))), /*#__PURE__*/ React.createElement(Input, {
        autoComplete: "tel-national",
        pattern: "[0-9]",
        pl: "4rem",
        placeholder: placeholder,
        ref: ref,
        type: "tel",
        value: phoneNumber,
        ...inputProps,
        onChange: handleNumberChange
    }));
});

//# sourceMappingURL=phone-input.js.map